import React from "react";
import { Smartphone, Tablet, Laptop, Monitor } from "lucide-react";
import type { ResponsiveSizes } from "../types";

interface FontWeightModalProps {
  isOpen: boolean;
  responsiveFontWeights: ResponsiveSizes;
  onFontWeightsChange: (weights: ResponsiveSizes) => void;
  onApply: () => void;
  onClose: () => void;
}

const fontWeightOptions = [
  { value: "", label: "None" },
  { value: "100", label: "Thin (100)" },
  { value: "200", label: "Extra Light (200)" },
  { value: "300", label: "Light (300)" },
  { value: "400", label: "Normal (400)" },
  { value: "500", label: "Medium (500)" },
  { value: "600", label: "Semi Bold (600)" },
  { value: "700", label: "Bold (700)" },
  { value: "800", label: "Extra Bold (800)" },
  { value: "900", label: "Black (900)" },
];

export const FontWeightModal: React.FC<FontWeightModalProps> = ({
  isOpen,
  responsiveFontWeights,
  onFontWeightsChange,
  onApply,
  onClose,
}) => {
  if (!isOpen) return null;

  const breakpoints = [
    { key: "xs", label: "Mobile (xs) - Base", icon: Smartphone },
    { key: "sm", label: "Tablet (sm) - 1080px", icon: Tablet },
    { key: "md", label: "Desktop (md) - 1230px", icon: Laptop },
    { key: "lg", label: "Large (lg) - 1430px", icon: Monitor },
    { key: "xl", label: "Extra Large (xl) - 1750px", icon: Monitor },
  ] as const;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg">
        <h3 className="text-lg font-semibold mb-4">Set Responsive Font Weights</h3>
        <div className="flex flex-col gap-4">
          {breakpoints.map(({ key, label, icon: Icon }) => (
            <div key={key} className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <Icon className="w-4 h-4" />
                {label}
              </label>
              <select
                value={responsiveFontWeights[key]}
                onChange={(e) => onFontWeightsChange({ ...responsiveFontWeights, [key]: e.target.value })}
                className="border border-gray-300 px-3 py-2 rounded-md text-sm bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-main/20 focus:border-primary-main"
              >
                {fontWeightOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          ))}

          <div className="flex gap-2 justify-end pt-2 border-t mt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium hover:bg-gray-50 transition-all"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onApply}
              className="px-4 py-2 bg-primary-main text-white rounded-md text-sm font-medium hover:bg-primary-main/90 transition-all"
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};


